import { useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { InterstitialOverlay } from "@/components/overlay";
import { useAppStore } from "@/store";
import { windowModule } from "@/modules/window";
import { toastModule } from "@/modules/toast";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { clientEnvModule } from "@/modules/client-env";
import { localStorageModule } from "@/modules/local-storage";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialog,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { useState } from "react";
import { CalendarAccountConfigurationList } from "@/components/calendar/calendar-account-configuration-list";

export const IntegrationsCalendarGoogleOAuthCallbackPage = observer(() => {
  const { store } = useAppStore();
  const location = useLocation();
  const googleCalendarStore = store.calendar.google;

  const [isLoading, setIsLoading] = useState(true);
  const [connectedSubjectId, setConnectedSubjectId] = useState<string | null>(null);
  const account = connectedSubjectId ? googleCalendarStore.accounts.get(connectedSubjectId) : null;

  useEffectOnMount(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get("error");
    const code = params.get("code");
    const state = params.get("state");

    const isDesktop = clientEnvModule.isDesktop();
    const storedState = localStorageModule.readGoogleCalendarOAuthState();

    // If we're in the web version and the OAuth state is not in localStorage,
    // it means the OAuth was initiated from Electron. Redirect back to Electron
    // so it can complete the connection using its stored state.
    if (!isDesktop && !storedState && state) {
      logger.info({
        message:
          "[IntegrationsCalendarGoogleOAuthCallbackPage] OAuth state not found in web localStorage, redirecting to Electron",
        info: { search: location.search },
      });

      const asyncLogic = async () => {
        try {
          const targetUrl = new URL(
            `mem://integrations/calendar/google/callback${location.search}`
          );
          await windowModule.navigateToExternal({ url: targetUrl });
        } catch (unknownError) {
          logger.error({
            message: "[IntegrationsCalendarGoogleOAuthCallbackPage] Failed to redirect to Electron",
            info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
          });
          toastModule.triggerToast({
            content: "Unable to complete Google Calendar connection. Please try again.",
          });
        } finally {
          // Always navigate back to the integrations calendar page because this callback page isn't useful anymore
          store.navigation.goToIntegrationsCalendar({ openInSameTab: true });
        }
      };

      void asyncLogic();
      return;
    }

    if (error) {
      toastModule.triggerToast({
        content: "Google Calendar connection failed. Please try again.",
      });
      store.navigation.goToIntegrationsCalendar({ openInSameTab: true });
      return;
    }

    if (!code) {
      toastModule.triggerToast({
        content: "Missing authorization code from Google. Please try connecting again.",
      });
      store.navigation.goToIntegrationsCalendar({ openInSameTab: true });
      return;
    }

    const redirectUri = windowModule.getGoogleCalendarOAuthRedirectUrl();
    const asyncLogic = async () => {
      try {
        const result = await store.calendar.google.completeConnection({ code, state, redirectUri });
        setConnectedSubjectId(result.subjectId);
        setIsLoading(false);
      } catch (_unknownError) {
        logger.error({
          message:
            "[IntegrationsCalendarGoogleOAuthCallbackPage] Failed to complete Google Calendar connection",
          info: { error: objectModule.safeErrorAsJson(_unknownError as Error) },
        });
        toastModule.triggerToast({
          content: "Unable to complete Google Calendar connection. Please try again.",
        });
        store.navigation.goToIntegrationsCalendar({ openInSameTab: true });
      }
    };

    void asyncLogic();
  });

  return (
    <InterstitialOverlay>
      <MDSDialog isOpen isDismissable={false}>
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Choose calendars to see in Mem
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphSm}>
            Select calendars to view in Mem and use for meeting notifications
          </MDSText>
          <CalendarAccountConfigurationList
            email={account?.googleAccountEmail ?? null}
            provider="google"
            calendars={account?.calendars ?? []}
            isUpdating={googleCalendarStore.isUpdatingCalendars}
            lastSyncedAt={account?.lastSyncedAt ?? null}
            subjectId={connectedSubjectId ?? undefined}
            onToggleCalendar={({ calendarId, isEnabled, subjectId }) => {
              void googleCalendarStore.toggleCalendar({ subjectId, calendarId, isEnabled });
            }}
            isLoading={isLoading}
          />
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            isDisabled={isLoading}
            onClick={() => store.navigation.goToIntegrationsCalendar({ openInSameTab: true })}
          >
            Next
          </MDSButton>
        </MDSButtonGroup>
      </MDSDialog>
    </InterstitialOverlay>
  );
});
